import React, { useEffect, useMemo, useRef, useState } from "react";
import Avatar from "@mui/material/Avatar";
import { IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ApiMethods from "../../../../ApiManager/ApiMethods";
import { useAlert } from "../../../context/AlertContext";
import { useMembers, refreshClientPackages } from "../../../data/membersStore";
import { selectCurrentPackage } from "../../../../common/utils/packageStatus";
import { compareMembersBy } from "./MembersSortControl";

// Same debounce as NewAppointmentFlow.jsx's own member search (SEARCH_DEBOUNCE_MS) — local
// filtering of an already-cached client list, so this exists purely to avoid re-filtering on
// every keystroke, not to mask network latency.
const SEARCH_DEBOUNCE_MS = 300;

const memberAvatarSx = (gender) => ({
  width: 30,
  height: 30,
  fontSize: "11px",
  fontWeight: 600,
  color: "black",
  backgroundColor: gender === "Male" ? "#97dfff" : "#FF8E8B",
});

// The "Dodaj vežbača" quick-add area (PopUpCard.jsx's expanded Booked Members card) — a
// deliberately tiny composition, not a second New Appointment: search box + at most one
// eligible-member result, reusing the exact same data sources and booking call New
// Appointment's own handleSave uses (useMembers' shared cache, selectCurrentPackage for
// eligibility/remaining-sessions, `auth/add_booking_cancelation` for the actual booking) so
// there's only one source of truth for "does this member have a usable package" and "how a
// booking gets created" — just a much smaller UI on top of them.
const QuickAddMemberPanel = ({
  appointment,
  bookedClientIds,
  onAdded,
  onCancel,
  // Whether the quick-add area is actually expanded (PopUpCard.jsx's `quickAddOpen`). This
  // panel stays mounted even while collapsed (so the grid-rows expand can animate), so the
  // search input must NOT be focused until it's genuinely open — otherwise iOS fires the
  // keyboard the instant the Booked Members popup opens, covering the member list.
  active,
  // Only autofocus the search on Desktop/Tablet — on Mobile the popped keyboard covers half
  // the sheet and the trainer taps the field themselves when ready (same rule as
  // MemberSelectorPopup's own `autoFocus={isDesktopTablet}`).
  desktop,
}) => {
  const { showAlert } = useAlert();
  const { clients, packagesByClient } = useMembers();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch(search.trim().toLowerCase()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(t);
  }, [search]);

  // Focus the search field only once the area is actually expanded (`active`) AND only on
  // Desktop/Tablet — the panel stays mounted the whole time the Booked Members popup is open
  // (for the expand animation), so focusing on mount would pop the iOS keyboard the moment that
  // popup opens; and even on the deliberate "Dodaj vežbača" tap, a Mobile keyboard covers half
  // the sheet, so there the trainer taps the field when ready instead.
  useEffect(() => {
    if (active && desktop) inputRef.current?.focus();
  }, [active, desktop]);

  // Only one row shown at a time, and only once there's something to search for — an empty box
  // intentionally shows nothing rather than an arbitrary "first" member. Eligibility is exactly
  // point 6's rule (active package with >=1 remaining session), via the same
  // `selectCurrentPackage` NewAppointmentFlow's own status line already uses — `status ===
  // "active"` there already means usedSessions < totalSessions, so nothing extra to check.
  const { match, hasQuery } = useMemo(() => {
    const q = debouncedSearch;
    if (!q) return { match: null, hasQuery: false };
    const eligible = clients
      .filter((c) => {
        const id = c.id ?? c.client_id;
        if (bookedClientIds.has(id)) return false;
        const haystack = `${c.first_name || ""} ${c.last_name || ""} ${
          c.email || ""
        } ${c.phone || ""}`.toLowerCase();
        return haystack.includes(q);
      })
      .map((client) => {
        const id = client.id ?? client.client_id;
        return { client, status: selectCurrentPackage(packagesByClient[id] || []) };
      })
      .filter((entry) => entry.status.status === "active")
      .sort((a, b) => compareMembersBy("first_name", "asc")(a.client, b.client));
    return { match: eligible[0] || null, hasQuery: true };
  }, [clients, packagesByClient, debouncedSearch, bookedClientIds]);

  const handleSelect = async (entry) => {
    if (saving) return;
    setSaving(true);
    const clientId = entry.client.id ?? entry.client.client_id;
    try {
      await ApiMethods.post("auth/add_booking_cancelation", {
        timeSlots: [
          {
            client_id: clientId,
            status: "book",
            start: appointment.start,
            timeSlot_id: appointment.id,
          },
        ],
      });
      showAlert("Vežbač je dodat u termin.", "success");
      // Same as every other booking mutation in this app — refresh just this client's cached
      // package/usage rows so Members and the package popup reflect the new booking.
      refreshClientPackages(clientId);
      onAdded();
    } catch (err) {
      console.log(err);
      if (err?.code === "NO_AVAILABLE_PACKAGE") {
        showAlert(err.message, "error");
      } else {
        showAlert("Greška prilikom dodavanja vežbača.", "error");
      }
      setSaving(false);
    }
  };

  return (
    <div className="hf-bp-quickadd-content">
      <div className="hf-bp-quickadd-searchrow">
        <SearchIcon fontSize="small" className="hf-bp-quickadd-search-icon" />
        <input
          ref={inputRef}
          type="text"
          className="hf-bp-quickadd-input"
          placeholder="Pretraži vežbača..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <IconButton
          size="small"
          aria-label="Zatvori pretragu"
          className="hf-bp-quickadd-close"
          onClick={onCancel}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {hasQuery &&
        (match ? (
          <div
            className={`hf-bp-quickadd-row${saving ? " saving" : ""}`}
            onClick={() => handleSelect(match)}
          >
            <Avatar sx={memberAvatarSx(match.client.gender)}>
              {match.client.first_name ? match.client.first_name[0].toUpperCase() : "?"}
              {match.client.last_name ? match.client.last_name[0].toUpperCase() : "?"}
            </Avatar>
            <span className="hf-bp-quickadd-name">
              {match.client.first_name} {match.client.last_name}
            </span>
            {saving ? (
              <span className="hf-btn-spinner" />
            ) : (
              <span className="hf-bp-quickadd-pkg">
                {match.status.totalSessions - match.status.usedSessions}/
                {match.status.totalSessions}
              </span>
            )}
          </div>
        ) : (
          <div className="hf-bp-quickadd-empty">Nema dostupnih vežbača</div>
        ))}
    </div>
  );
};

export default QuickAddMemberPanel;
